import { useEffect, useMemo, useState } from 'react'
import { StageViewer } from '@/components/stage'
import {
  CHAINS,
  CLEARANCE_MIN,
  GHOST_PHASES,
  HULL_SAMPLES,
  HULL_SECTIONS,
  JOINT_LABELS,
  PLANE_LABELS,
  SIDES,
  checkManual,
  computeEnvelope,
  envelopeBodies,
  manualKey,
  workspace,
  type AxisLimits,
  type AxisTravel,
  type Capsule,
  type ChainSpec,
  type HumanoidModel,
  type ManualPose,
  type PlaneKey,
  type Side,
  type Skeleton,
  type TravelResult,
  type Workspace,
} from '@/domain/hs1'
import { Alert, Badge, Button, Card, Table, Td, Th } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, Slider, axisRow, num } from '@/sim/parts'

/**
 * Die drei Grenzlagen-Abschnitte der Freigangseite.
 *
 * Sie liegen neben der Seite und nicht in ihr, weil die Seite sonst achthundert
 * Zeilen hätte und niemand mehr fände, welcher Abschnitt welche Frage
 * beantwortet. Jeder hier ist eine eigene Frage:
 *
 *   Achsgrenzen  — wie weit darf EINE Achse, wenn alles andere ruht?
 *   Arbeitsraum  — wohin kommt eine KETTE, wenn alle ihre Achsen mitgehen?
 *   Pose         — steht DIESE eine Stellung, die ich gerade meine?
 */

const mm = (value: number): string => `${num(value * 1000, 1)}`

export const axisLabel = (joint: string, plane: PlaneKey): string =>
  `${JOINT_LABELS[joint] ?? joint} · ${PLANE_LABELS[plane]}`

/* -------------------------------------------------------------------------
   Achsgrenzen
   ------------------------------------------------------------------------- */

export function AxisLimitsSection({ travel, rows }: { travel: TravelResult; rows: AxisLimits[] }) {
  const { focus, setFocus } = useSimulation()
  const [side, setSide] = useState<Side>('R')

  /* Wie bei den Antrieben: die Marke gehört zu dieser Liste und nicht zur
     Sitzung. */
  useEffect(() => () => setFocus(null), [setFocus])

  const shown = rows.filter((r) => r.side === side)
  const blocked = rows.filter(
    (r) =>
      r.side === side && (r.binding.lo === 'kollisionsfrei' || r.binding.hi === 'kollisionsfrei'),
  )
  const exceeded = shown.filter((r) => r.exceeded)

  return (
    <div className="stack">
      <div className="metrics">
        <Metric
          label="Freigang in der Grundstellung"
          value={`${mm(travel.restClearance)} mm`}
          note={travel.restPair ?? 'kein Paar in der Nähe'}
          warn={travel.restClearance < CLEARANCE_MIN}
        />
        <Metric
          label="Achsen mit Anschlag"
          value={`${blocked.length} von ${shown.length}`}
          note="ein Hüllkörper begrenzt sie vor dem Modul"
        />
        <Metric
          label="Bewegung über der Grenze"
          value={exceeded.length}
          note={
            exceeded.length ? exceeded.map((r) => axisLabel(r.joint, r.plane)).join(' · ') : 'keine'
          }
          warn={exceeded.length > 0}
        />
      </div>

      <Card
        title="Drei Grenzen je Achse"
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {SIDES.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={side === s ? 'primary' : 'ghost'}
                onClick={() => setSide(s)}
              >
                {s === 'R' ? 'rechts' : 'links'}
              </Button>
            ))}
          </div>
        }
      >
        <Table className="limit-table">
          <thead>
            <tr>
              <Th>Achse</Th>
              <Th numeric>gefordert</Th>
              <Th numeric>modulseitig</Th>
              <Th numeric>kollisionsfrei</Th>
              <Th numeric>gilt</Th>
              <Th numeric>Ausnutzung</Th>
              <Th>Was begrenzt</Th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              /* Wie in der Antriebsliste: ein Klick markiert das Gelenk im
                 Bühnenfenster darüber. Gerade hier zählt es doppelt — eine
                 Achse, die durch einen Hüllkörper begrenzt wird, will man
                 ansehen und nicht nur lesen. */
              <tr
                key={`${row.joint}|${row.plane}`}
                {...axisRow(focus, setFocus, {
                  joint: row.joint,
                  side: row.side,
                  label: axisLabel(row.joint, row.plane),
                })}
              >
                <Td>{axisLabel(row.joint, row.plane)}</Td>
                <Td numeric>
                  {row.required ? (
                    <span className={row.exceeded ? 'cell-warn' : ''}>
                      {num(row.required.lo, 0)} … {num(row.required.hi, 0)}°
                    </span>
                  ) : (
                    <span className="cell-muted">—</span>
                  )}
                </Td>
                <Td numeric>
                  <span
                    className={
                      row.binding.lo === 'modulseitig' || row.binding.hi === 'modulseitig'
                        ? ''
                        : 'cell-muted'
                    }
                  >
                    {num(row.module.lo, 0)} … {num(row.module.hi, 0)}°
                  </span>
                </Td>
                <Td numeric>
                  <span
                    className={
                      row.binding.lo === 'kollisionsfrei' || row.binding.hi === 'kollisionsfrei'
                        ? 'cell-tight'
                        : 'cell-muted'
                    }
                  >
                    {num(row.free.lo, 1)} … {num(row.free.hi, 1)}°
                  </span>
                </Td>
                <Td numeric>
                  <b>
                    {num(row.effective.lo, 1)} … {num(row.effective.hi, 1)}°
                  </b>
                </Td>
                <Td numeric>
                  {row.use === null ? (
                    <span className="cell-muted">—</span>
                  ) : (
                    <span className={row.use > 0.9 ? 'cell-warn' : ''}>
                      {num(row.use * 100, 0)} %
                    </span>
                  )}
                </Td>
                <Td>
                  <span className="cell-muted">{describeBinding(travel.travels, row)}</span>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  )
}

function describeBinding(travels: AxisTravel[], row: AxisLimits): string {
  const travel = travels.find(
    (t) => t.joint === row.joint && t.plane === row.plane && t.side === row.side,
  )
  if (!travel) return '—'
  const parts: string[] = []
  if (travel.loBlocker) parts.push(`unten ${travel.loBlocker}`)
  if (travel.hiBlocker) parts.push(`oben ${travel.hiBlocker}`)
  return parts.length > 0 ? parts.join(' · ') : 'nur das Modul'
}

/* -------------------------------------------------------------------------
   Arbeitsraum
   ------------------------------------------------------------------------- */

/** Wie viele Stützstellen je Achse zur Wahl stehen. */
const STEPS = [3, 5, 7] as const

export function WorkspaceSection({
  model,
  skeleton,
  travels,
}: {
  model: HumanoidModel
  skeleton: Skeleton
  travels: AxisTravel[]
}) {
  const [chain, setChain] = useState<ChainSpec>(CHAINS[0])
  const [steps, setSteps] = useState<number>(5)
  const [side, setSide] = useState<Side>('R')

  const space = useMemo(
    () => workspace(model, skeleton, chain, side, travels, steps),
    [model, skeleton, chain, side, travels, steps],
  )

  return (
    <div className="stack">
      <div className="metrics">
        <Metric
          label="Reichweite"
          value={`${num(space.reach * 1000, 0)} mm`}
          note="grösster Abstand von der Beckenmitte"
        />
        <Metric
          label="Kollisionsfrei"
          value={`${space.freeCount} von ${space.points.length}`}
          note={`${num((100 * space.freeCount) / space.points.length, 0)} % der abgetasteten Stellungen`}
        />
        <Metric
          label="Rechteckhülle"
          value={`${num((space.box.max.x - space.box.min.x) * 1000, 0)} × ${num(
            (space.box.max.y - space.box.min.y) * 1000,
            0,
          )} × ${num((space.box.max.z - space.box.min.z) * 1000, 0)} mm`}
          note="eine OBERE Schranke, kein erreichter Raum"
        />
        <Metric
          label="Abtastung"
          value={`${steps}^${chain.axes.length}`}
          note={`${space.points.length} Stellungen, je eine volle Freigangsprüfung`}
        />
      </div>

      {/*
        Die Auswahl steht im KÖRPER der Karte und nicht in ihrem Kopf. Drei
        Kettennamen, zwei Seiten und drei Auflösungen sind acht Schalter; im Kopf
        drängen sie die Überschrift auf zwei Zeichen Breite, und aus „Arm bis
        Handwurzel" wird ein Wortbruch.
      */}
      <Card title="Erreichbare Punkte" eyebrow={chainAxes(chain)}>
        <div className="stack stack--tight">
          <div className="recorder__chips">
            {CHAINS.map((c) => (
              <Button
                key={c.key}
                size="sm"
                variant={chain.key === c.key ? 'primary' : 'ghost'}
                onClick={() => setChain(c)}
              >
                {c.label}
              </Button>
            ))}
          </div>
          <div className="recorder__chips">
            {SIDES.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={side === s ? 'primary' : 'ghost'}
                onClick={() => setSide(s)}
              >
                {s === 'R' ? 'rechts' : 'links'}
              </Button>
            ))}
            {STEPS.map((n) => (
              <Button
                key={n}
                size="sm"
                variant={steps === n ? 'primary' : 'ghost'}
                onClick={() => setSteps(n)}
              >
                {n} Stützstellen
              </Button>
            ))}
          </div>
          <WorkspacePlot space={space} />
          <p className="page-lead">
            Zwei Schnitte durch dieselbe Punktwolke, bezogen auf die Beckenmitte. Gefüllt: die
            Stellung ist kollisionsfrei. Offen: sie liegt zwar in den Achsgrenzen, die Kette trifft
            aber einen anderen Körper.
          </p>
        </div>
      </Card>
    </div>
  )
}

/**
 * Zwei Schnitte statt eines Volumens.
 *
 * Sagittal (x nach vorn, y nach oben) und frontal (z zur Seite, y nach oben).
 * Ein perspektivisches Bild einer Punktwolke sieht besser aus und lässt sich
 * nicht ablesen — hier soll man Millimeter abgreifen können.
 */
function WorkspacePlot({ space }: { space: Workspace }) {
  const views: { key: string; title: string; ax: 'x' | 'z'; label: string }[] = [
    { key: 'sag', title: 'Sagittal', ax: 'x', label: 'vorn →' },
    { key: 'fro', title: 'Frontal', ax: 'z', label: 'seitlich →' },
  ]

  /* Ein gemeinsamer Massstab für beide Schnitte: verschiedene Massstäbe
     nebeneinander lassen den einen Raum grösser aussehen als den anderen. */
  let span = 0
  for (const point of space.points) {
    span = Math.max(span, Math.abs(point.p.x), Math.abs(point.p.y), Math.abs(point.p.z))
  }
  span = Math.max(0.1, span * 1.05)

  return (
    <div className="wsplot">
      {views.map((view) => (
        <figure key={view.key} className="wsplot__panel">
          <figcaption className="eyebrow">
            {view.title} · {view.label} · ±{num(span * 1000, 0)} mm
          </figcaption>
          <svg viewBox="-105 -105 210 210" role="img" aria-label={`Arbeitsraum ${view.title}`}>
            <g className="wsplot__grid">
              <line x1="-100" y1="0" x2="100" y2="0" />
              <line x1="0" y1="-100" x2="0" y2="100" />
              <circle cx="0" cy="0" r="50" />
              <circle cx="0" cy="0" r="100" />
            </g>
            {space.points.map((point, i) => {
              const h = (view.ax === 'x' ? point.p.x : point.p.z) / span
              const v = point.p.y / span
              return (
                <circle
                  key={i}
                  cx={h * 100}
                  cy={-v * 100}
                  r={point.free ? 1.6 : 1.1}
                  className={point.free ? 'wsplot__free' : 'wsplot__blocked'}
                />
              )
            })}
            {/* Die Beckenmitte, auf die alles bezogen ist. */}
            <circle cx="0" cy="0" r="2.4" className="wsplot__origin" />
          </svg>
        </figure>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------------------
   Pose von Hand
   ------------------------------------------------------------------------- */

export function ManualSection({
  model,
  skeleton,
  travels,
  pose,
  setPose,
}: {
  model: HumanoidModel
  skeleton: Skeleton
  travels: AxisTravel[]
  pose: ManualPose | null
  setPose: (pose: ManualPose | null) => void
}) {
  const [side, setSide] = useState<Side>('R')
  const active = pose ?? {}
  const check = useMemo(
    () => checkManual(model, skeleton, active, travels),
    [model, skeleton, active, travels],
  )

  const shown = travels.filter((t) => t.side === side)

  const set = (key: string, value: number) => setPose({ ...active, [key]: value })

  return (
    <div className="stack">
      {/*
        Das Bühnenfenster gehört hierher und nicht nur auf die Seite Simulation:
        eine Pose von Hand zu stellen und sie in einer TABELLE zu prüfen, heisst
        sich die Stellung vorstellen zu müssen, die man gerade prüft. Hier steht
        sie, und man kann um sie herumfahren.
      */}
      <StageViewer height={420} />

      {pose === null ? (
        <Alert tone="info" title="Die Bühne läuft noch den Gang">
          Sobald Sie eine Achse verstellen, hält die Figur an und zeigt genau die eingegebene
          Stellung — auf den Boden gestellt, mit Freigangsprüfung. Der Rechenkern bleibt davon
          unberührt: Gang, Kinetik und Antriebe sind weiter die des gewählten Gangbilds. Eine
          Grenzlage ist eine Frage an die Konstruktion und keine Betriebsbedingung.
        </Alert>
      ) : (
        <div className="metrics">
          <Metric
            label="Freigang"
            value={`${mm(check.clearance)} mm`}
            note={check.pair ?? 'kein Paar in der Nähe'}
            warn={check.clearance < CLEARANCE_MIN}
          />
          <Metric
            label="Beckenhöhe"
            value={`${num(check.pelvisHeight * 1000, 0)} mm`}
            note={
              Math.abs(check.drop) < 5e-4
                ? 'wie in der Grundstellung — der Körper steht auf dem Boden'
                : `${num(Math.abs(check.drop) * 1000, 0)} mm ${
                    check.drop > 0 ? 'tiefer' : 'höher'
                  } als in der Grundstellung`
            }
          />
          <Metric
            label="Ausserhalb der Grenze"
            value={check.outside.length}
            note={
              check.outside.length
                ? check.outside.map((o) => axisLabel(o.joint, o.plane)).join(' · ')
                : 'keine Achse'
            }
            warn={check.outside.length > 0}
          />
          <Metric
            label="Von Hand gestellt"
            value={check.moved === 1 ? 'eine Achse' : `${check.moved} Achsen`}
          />
        </div>
      )}

      {pose !== null && check.clearance < CLEARANCE_MIN && (
        <Alert tone="warning" title="Diese Stellung geht nicht">
          {check.pair} stehen {mm(check.clearance)} mm auseinander — gefordert sind{' '}
          {(CLEARANCE_MIN * 1000).toFixed(0)} mm. Jede Achse für sich liegt dabei in ihrem Bereich;
          es ist die Kombination, die anschlägt. Die Achsgrenzen prüfen eine Achse bei ruhendem
          Rest und können das nicht sehen.
        </Alert>
      )}

      <Card
        title="Gelenkwinkel"
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {SIDES.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={side === s ? 'primary' : 'ghost'}
                onClick={() => setSide(s)}
              >
                {s === 'R' ? 'rechts' : 'links'}
              </Button>
            ))}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPose(null)}
              disabled={pose === null}
            >
              Zurück zum Gang
            </Button>
          </div>
        }
      >
        <div className="sliders">
          {shown.map((travel) => {
            const key = manualKey(travel.joint, travel.plane, travel.side)
            const value = active[key] ?? travel.rest
            const out = check.outside.some(
              (o) => o.joint === travel.joint && o.plane === travel.plane && o.side === travel.side,
            )
            return (
              <div key={key} className={out ? 'manual__out' : undefined}>
                <Slider
                  label={
                    <>
                      {axisLabel(travel.joint, travel.plane)}
                      {out && <Badge tone="warning">ausserhalb</Badge>}
                    </>
                  }
                  value={value}
                  /* GEFAHREN WIRD IM KOLLISIONSFREIEN BEREICH. Den modulseitigen
                     anzubieten hiesse, einen Regler zu bauen, dessen Enden
                     verboten sind — und wer ihn dorthin schiebt, sähe eine
                     Warnung statt einer Grenze. */
                  min={travel.lo}
                  max={travel.hi}
                  step={0.5}
                  unit="°"
                  decimals={1}
                  onChange={(v) => set(key, v)}
                />
              </div>
            )
          })}
        </div>
      </Card>
    </div>
  )
}

/* -------------------------------------------------------------------------
   Die Bewegungsanforderung
   ------------------------------------------------------------------------- */

/**
 * Der Winkelbereich, den die gerechnete Bewegung je Achse tatsächlich braucht.
 *
 * Aus den Winkeltabellen des Gangs und nicht aus einer zweiten Rechnung: die
 * Zahl, die hier als „gefordert" steht, muss dieselbe sein, die auf der
 * Antriebsseite als Bewegungsbereich steht.
 */
export function demandFrom(
  angles: Record<string, Partial<Record<PlaneKey, Record<Side, Float32Array>>>>,
): (joint: string, plane: PlaneKey, side: Side) => { lo: number; hi: number } | null {
  return (joint, plane, side) => {
    const track = angles[joint]?.[plane]?.[side]
    if (!track || track.length === 0) return null
    let lo = Infinity
    let hi = -Infinity
    for (const value of track) {
      if (value < lo) lo = value
      if (value > hi) hi = value
    }
    /* Eine Achse, die sich nicht bewegt, fordert nichts. Sie mit einem Bereich
       von null Grad zu führen ergäbe eine Ausnutzung von null Prozent und sähe
       aus wie eine Reserve — sie ist aber schlicht nicht in Gebrauch. */
    if (hi - lo < 1e-6) return null
    return { lo, hi }
  }
}

export function chainAxes(chain: ChainSpec): string {
  return chain.axes.map((a) => axisLabel(a.joint, a.plane)).join(' · ')
}

/* -------------------------------------------------------------------------
   Umhüllung — was ein Bauteil im Zyklus überstreicht
   ------------------------------------------------------------------------- */

/** Die drei Gruppen der Freigangsprüfung, ausgeschrieben. */
const GROUP_LABELS: Record<Capsule['group'], string> = {
  bein: 'Bein',
  arm: 'Arm',
  rumpf: 'Rumpf',
}

/**
 * DIE RECHNUNG LÄUFT AUF KNOPFDRUCK und nicht bei jeder Änderung.
 *
 * Eine Hülle über sechzehn Bauteile kostet knapp hundert Millisekunden. Das ist
 * zu viel für einen Haken in einer Tabelle und zu wenig, um dafür einen
 * Nebenläufer aufzumachen — also wird sie gerechnet, wenn jemand eine
 * Darstellung einschaltet, und danach nur noch, wenn sich die Bewegung ändert.
 *
 * SIE WIRD IM BÜHNENFENSTER GEZEICHNET und nicht in einem eigenen Bild. Ein
 * Hüllkörper ohne die Figur darin beantwortet nichts: die Frage ist ja gerade,
 * wo der Raum RELATIV ZUM KÖRPER liegt.
 */
export function EnvelopeSection({
  model,
  skeleton,
}: {
  model: HumanoidModel
  skeleton: Skeleton
}) {
  const { simulation, envelope: view, setEnvelope } = useSimulation()

  const bodies = useMemo(() => envelopeBodies(model, skeleton), [model, skeleton])
  const [side, setSide] = useState<'B' | Side>('B')
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set(bodies.map((b) => b.key)))
  const [ghosts, setGhosts] = useState(false)
  const [hulls, setHulls] = useState(false)
  const on = ghosts || hulls

  /*
    Der tiefste Punkt steht in der Tabelle, bevor irgendetwas eingeschaltet ist
    — er ist die eine Zahl, für die man kein Bild braucht. Er kostet eine
    Abtastung ohne Hüllen und ohne Mehrfachbild.
  */
  const lowest = useMemo(() => {
    const out = computeEnvelope(model, skeleton, simulation.gait, simulation.ground, {
      ghostPhases: 2,
      sections: 1,
    })
    return new Map<string, number>(out.bodies.map((b) => [b.key, b.lowest]))
  }, [model, skeleton, simulation.gait, simulation.ground])

  /* Die Umhüllung selbst: nur, wenn etwas gezeigt wird. */
  const envelope = useMemo(() => {
    if (!on) return null
    const t0 = performance.now()
    const out = computeEnvelope(model, skeleton, simulation.gait, simulation.ground, {
      keys: picked,
    })
    return { out, ms: Math.round(performance.now() - t0) }
  }, [on, model, skeleton, simulation.gait, simulation.ground, picked])

  /*
    Was die Bühne zeichnet, wird hier hineingereicht und beim Verlassen der
    Seite wieder abgeräumt. Ohne das Aufräumen bliebe eine Hülle für den Rest
    der Sitzung an einer Figur hängen, die auf einer anderen Seite steht.
  */
  useEffect(() => {
    setEnvelope(envelope ? { envelope: envelope.out, selected: picked, ghosts, hulls } : null)
  }, [envelope, picked, ghosts, hulls, setEnvelope])

  useEffect(() => () => setEnvelope(null), [setEnvelope])

  const chooseSide = (next: 'B' | Side) => {
    setSide(next)
    setPicked(
      new Set(
        bodies
          .filter((b) => next === 'B' || b.side === undefined || b.side === next)
          .map((b) => b.key),
      ),
    )
  }

  const toggle = (key: string) =>
    setPicked((set) => {
      const out = new Set(set)
      if (out.has(key)) out.delete(key)
      else out.add(key)
      return out
    })

  const shown = view?.envelope.bodies.length ?? 0

  return (
    <div className="stack">
      <StageViewer height={460} />

      <div className="metrics">
        <Metric label="Bauteile" value={`${picked.size} von ${bodies.length}`} note="gewählt" />
        <Metric
          label="Lagen im Mehrfachbild"
          value={String(GHOST_PHASES)}
          note="gleichmässig über den Zyklus"
        />
        <Metric
          label="Abschnitte je Hülle"
          value={String(HULL_SECTIONS)}
          note={`aus ${HULL_SAMPLES} Phasen`}
        />
        <Metric
          label="Gerechnet"
          value={envelope ? `${envelope.ms} ms` : '—'}
          note={on ? `${shown} Körper` : 'nichts eingeschaltet'}
        />
      </div>

      <Card
        title="Darstellung"
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {(['B', 'R', 'L'] as const).map((key) => (
              <Button
                key={key}
                size="sm"
                variant={side === key ? 'primary' : 'ghost'}
                onClick={() => chooseSide(key)}
              >
                {key === 'B' ? 'Beide Seiten' : key === 'R' ? 'nur rechts' : 'nur links'}
              </Button>
            ))}
          </div>
        }
      >
        <div className="env__modes">
          <Button
            size="sm"
            variant={ghosts ? 'primary' : 'secondary'}
            onClick={() => setGhosts((v) => !v)}
          >
            Mehrfachbild
          </Button>
          <Button
            size="sm"
            variant={hulls ? 'primary' : 'secondary'}
            onClick={() => setHulls((v) => !v)}
          >
            Hüllvolumen
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setGhosts(false)
              setHulls(false)
            }}
          >
            aus
          </Button>
        </div>

        <p className="page-lead">
          Jedes Bauteil erzeugt beim Durchlaufen des Zyklus einen überstrichenen Raum.{' '}
          <b>Mehrfachbild</b> zeigt das Bauteil an {GHOST_PHASES} Phasen übereinander,{' '}
          <b>Hüllvolumen</b> legt einen geschlossenen Körper darum — in {HULL_SECTIONS} Abschnitten,
          damit er sich enger anlegt als eine einzige Hülle über den ganzen Zyklus. Beide sind
          bewusst etwas zu gross gerechnet.
        </p>
      </Card>

      <Card flush title="Bauteile">
        <Table>
          <thead>
            <tr>
              <Th>Bauteil</Th>
              <Th>Gruppe</Th>
              <Th align="right">tiefster Punkt</Th>
              <Th align="right">Radius</Th>
              <Th align="right">zeigen</Th>
            </tr>
          </thead>
          <tbody>
            {bodies.map((body) => {
              const low = lowest.get(body.key) ?? 0
              return (
                <tr key={body.key}>
                  <Td>{body.label}</Td>
                  <Td>{GROUP_LABELS[body.group]}</Td>
                  {/* Unter null heisst: das Bauteil taucht unter die Standebene.
                      Beim Fuss richtig, bei allem anderen ein Befund. */}
                  <Td
                    align="right"
                    className={low < 0 && body.group !== 'bein' ? 'env__under' : undefined}
                  >
                    {num(low * 1000, 0)} mm
                  </Td>
                  <Td align="right">{num(body.radius * 1000, 0)} mm</Td>
                  <Td align="right">
                    <input
                      type="checkbox"
                      checked={picked.has(body.key)}
                      onChange={() => toggle(body.key)}
                      aria-label={`${body.label} zeigen`}
                    />
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Card>
    </div>
  )
}
